import { computed, ref, toRef, watch, watchEffect, provide, useTemplateRef } from 'vue';
import { getSlideDetailStore, PanelInfo } from '@/common/stores/slide-detail';
import { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { getInitPeriodInfo } from '@/common/components/molecules/timeController';
import { storeToRefs } from 'pinia';
import { useInternational } from '@/common/locale';
import { useTargetTagStore } from '@/common/stores/target-tags';
import { useGetV7NdmDeviceMonitoringAlertMessageByDeviceIdAndAlertRuleId } from '@/api-client/ndm/queries/ndm-monitoring-controller/ndm-monitoring-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { AlertTreeRow, useAlertTreeGrid } from '@/alert/components/alertGrid/alertGrid.uses';
import { useAlertRuleDetailAdapter } from '@/alert/adapter/alertRuleDetail.adapter';
import { getFilterItemsByGridColumns } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { useEvGridFilterSearch } from '@/common/components/molecules/filterSearch/filterSearch.uses';
import { useGetV9AlertsAlertRulesCurrent } from '@/api-client/alert/queries/alert-common-v9-controller/alert-common-v9-controller';
import { ALERT_PLATFORM_TYPES } from '@/common/define/common.define';
import { SegmentedControlOption } from '@/common/components/molecules/control/segmentedControl/SegmentedControl.types';
import { useAutoValueItems } from '@/common/composables/useAutoValueItems';
import {
  EXTERNAL_FILTER_SEARCH_KEY,
  type FilterSearchExposed,
} from '@/common/components/molecules/filterSearch/filterSearch.type';

export interface Props {
  isShow: boolean;
  data?: {
    addition?: {
      deviceId: string;
      networkDeviceName: string;
    };
  };
}

// value를 통해 Syslog, Trap 외에 그냥 일반 Alert 값이 들어올수도 있음.
export const isEventType = (value: string | number): boolean => {
  const cellValue = `${value}`.toLowerCase();
  switch (cellValue) {
    case 'syslog':
    case 'trap':
      return true;
    default:
      return false;
  }
};

const setup = (props: Props) => {
  const { t } = useInternational();
  const isShowAlertOnly = ref<boolean>(false);

  const VIEW_ALERT_TOGGLE: SegmentedControlOption[] = [
    {
      label: t('WORD.LIST'),
      icon: 'icon-outlined-format-list-bulleted',
      value: 'list',
    },
    {
      label: t('WORD.BAR'),
      icon: 'icon-bar-chart',
      value: 'bar',
    },
  ];

  const slideDetailStore = getSlideDetailStore();
  const { setIsDetailLoading } = slideDetailStore;
  const { selectedSlideTabPanel: curTabInfo } = storeToRefs(slideDetailStore);

  const originAlertRows = ref<AlertTreeRow[]>([]);
  const onlyAlertRows = ref<AlertTreeRow[]>([]);
  const rows = computed<AlertTreeRow[]>(() => {
    if (isShowAlertOnly.value) {
      return onlyAlertRows.value;
    }
    return originAlertRows.value;
  });

  const isOpenMessageWindow = ref<boolean>(false);
  const isShowRef = toRef(props, 'isShow');

  const timePeriodInfo = ref<TimePeriodInfo>({
    ...getInitPeriodInfo(),
    isPaused: !props.isShow,
  });

  const messageText = ref('');

  const networkInfo = ref<{
    facility?: string;
    severity?: string;
    specificTrapCode?: string;
    genericTrapType?: string;
    enterpriseOid?: string;
  }>({});

  const { fetchAllData: fetchAllTagsData } = useTargetTagStore();
  const { alertColumns, makeAlertTreeRows } = useAlertTreeGrid({ columnOption: 'searchable' });

  const viewToggle = ref<string>(VIEW_ALERT_TOGGLE[0].value);
  const isListAlertView = computed(() => viewToggle.value === VIEW_ALERT_TOGGLE[0].value);

  const filterItems = getFilterItemsByGridColumns(alertColumns);
  const { filterSearchResultMV, filterTreeGridByFilterSearch } = useEvGridFilterSearch({
    columns: computed(() => alertColumns),
    filterItems,
  });

  const autoValueItems = useAutoValueItems({
    rows: computed(() => originAlertRows.value),
    filterItems: computed(() => filterItems),
    childrenKey: 'children',
  });

  const filterSearchRef = useTemplateRef<FilterSearchExposed>('filterSearchRef');

  provide(EXTERNAL_FILTER_SEARCH_KEY, {
    clear: () => {
      filterSearchResultMV.value = undefined;
      filterSearchRef.value?.clearFilterSearchToken();
    },
  });

  const filteredAlertRows = ref<AlertTreeRow[]>([]);
  watchEffect(() => {
    filteredAlertRows.value = filterTreeGridByFilterSearch(rows.value, filterSearchResultMV.value);
  });

  const deviceId = computed<string>(
    () =>
      props?.data?.addition?.deviceId ??
      (curTabInfo.value as PanelInfo<'Network Device'>)?.addition?.deviceId ??
      '',
  );

  const alertQueryParams = computed(() => ({
    targetIds: [deviceId.value],
    platformTypes: [ALERT_PLATFORM_TYPES.NETWORK_DEVICE],
  }));

  const isAlertQueryEnabled = computed(() => isShowRef.value && !!deviceId.value);

  // Alert Grid Query
  const {
    data: alertQueryData,
    isPending: isAlertPending,
    refetch: refetchAlert,
  } = useGetV9AlertsAlertRulesCurrent(alertQueryParams, {
    request: {
      frameName: FRAME_NAMES.NETWORK_DEVICE_DETAIL.ALERT,
    },
    query: {
      enabled: isAlertQueryEnabled,
    },
  });

  const processAlertData = (responseData: typeof alertQueryData.value) => {
    if (!responseData) return;

    const data = responseData?.data?.data ?? [];
    originAlertRows.value = makeAlertTreeRows({ data });
    onlyAlertRows.value = originAlertRows.value.filter((item) => {
      if (!item.alertLevel) {
        return false;
      }
      return item.alertLevel.toLowerCase() !== 'normal';
    });
  };

  const selectedAlertRuleId = ref<string>('');
  const selectedEventType = ref<string>('');

  const isMessageQueryEnabled = computed(
    () =>
      !!deviceId.value &&
      !!selectedAlertRuleId.value &&
      (selectedEventType.value === 'Syslog' || selectedEventType.value === 'Trap'),
  );

  const messageQueryParams = computed(() => ({
    eventType: selectedEventType.value,
  }));

  // Message Query
  const { data: messageQueryData } =
    useGetV7NdmDeviceMonitoringAlertMessageByDeviceIdAndAlertRuleId(
      deviceId,
      selectedAlertRuleId,
      messageQueryParams,
      {
        query: {
          enabled: isMessageQueryEnabled,
        },
      },
    );

  const processMessageData = (responseData: typeof messageQueryData.value) => {
    if (!responseData) return;

    const data = responseData?.data?.data?.[0];
    if (!data) return;

    if (selectedEventType.value === 'Syslog') {
      const { facility, severity, message } = data.sysLogMessage || {};
      networkInfo.value = { facility, severity };
      messageText.value = message || '';
      return;
    }

    if (selectedEventType.value === 'Trap') {
      const { genericTrapType, specificTrapCode, enterpriseOid, message } =
        data.snmpTrapMessage || {};
      networkInfo.value = { genericTrapType, specificTrapCode, enterpriseOid };
      messageText.value = message || '';
      return;
    }

    messageText.value = '';
  };

  const onRefresh = async (info: TimePeriodInfo) => {
    timePeriodInfo.value = info;
    if (props.isShow && !info.isPaused) {
      await refetchAlert();
    }
  };

  const onShowMessageBox = (item: AlertTreeRow) => {
    const eventType = item.value as string;

    if (eventType !== 'Syslog' && eventType !== 'Trap') {
      return;
    }

    isOpenMessageWindow.value = true;
    timePeriodInfo.value.isPaused = true;

    selectedAlertRuleId.value = item.alertRuleId ?? '';
    selectedEventType.value = eventType;
  };

  const { openAlertRuleDetail } = useAlertRuleDetailAdapter();
  const onClickCell = async ({ field, row }: { field: string; row: AlertTreeRow }) => {
    if (field === 'ruleName') {
      await fetchAllTagsData();
      openAlertRuleDetail({ params: { ...row }, selectedTab: 'history' });
    }
    if (field === 'value') {
      onShowMessageBox(row);
    }
  };

  watch(
    alertQueryData,
    (data) => {
      processAlertData(data);
    },
    { immediate: true },
  );

  watch(
    messageQueryData,
    (data) => {
      processMessageData(data);
    },
    { immediate: true },
  );

  watch(isAlertPending, (pending) => {
    if (isShowRef.value) {
      setIsDetailLoading(pending);
    }
  });

  return {
    VIEW_ALERT_TOGGLE,

    alertColumns,
    filteredAlertRows,
    timePeriodInfo,
    isShowAlertOnly,
    viewToggle,
    isListAlertView,
    filterSearchResultMV,
    filterItems,
    autoValueItems,

    isOpenMessageWindow,
    networkInfo,
    messageText,

    onRefresh,
    onClickCell,
    t,
  };
};

export { setup };
